/**
 * ShareModal - Enhanced sharing modal for collaboration spaces
 * Features: Team invites, Email invites, Public link sharing, Guest management
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Users, Link2, Mail, Copy, Check, Loader2, Trash2,
  Globe, Lock, RefreshCw, UserPlus, ExternalLink
} from 'lucide-react';
import { collaborationApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

// Avatar positions from the avatar sheet (9 columns x 4 rows = 36 avatars)
const AVATAR_SHEET_URL = 'https://customer-assets.emergentagent.com/job_b353f328-c785-48ef-8d8b-6ae791a336a7/artifacts/99xqh56e_set-of-funny-animal-portraits-vector.jpg';

const getAvatarStyle = (avatarId) => {
  // 9 columns x 4 rows grid
  const cols = 9;
  const row = Math.floor((avatarId - 1) / cols);
  const col = (avatarId - 1) % cols;
  return {
    backgroundImage: `url(${AVATAR_SHEET_URL})`,
    backgroundSize: '900% 400%',
    backgroundPosition: `${col * 12.5}% ${row * 33.33}%`,
  };
};

const ShareModal = ({
  open,
  onOpenChange,
  spaceId,
  spaceName,
  isOwner = false,
  collaborators = [],
  onCollaboratorsChange,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('people');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('edit'); // 'edit' | 'view'
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Share settings
  const [shareSettings, setShareSettings] = useState({
    access_type: 'private',
    link_permission: 'view',
    public_link_token: null,
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Guests
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);

  // Load share settings when modal opens
  useEffect(() => {
    if (open && spaceId && isOwner) {
      loadShareSettings();
      loadGuests();
    }
  }, [open, spaceId, isOwner]);

  const loadShareSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await collaborationApi.getShareSettings(spaceId);
      setShareSettings(response.data);
    } catch (err) {
      console.error('Failed to load share settings:', err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadGuests = async () => {
    try {
      setLoadingGuests(true);
      const response = await collaborationApi.listGuests(spaceId);
      setGuests(response.data || []);
    } catch (err) {
      console.error('Failed to load guests:', err);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleInviteEmail = async () => {
    if (!inviteEmail.trim()) return;
    
    setInviting(true);
    try {
      await collaborationApi.inviteCollaborator(spaceId, inviteEmail.trim(), inviteRole);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('edit');
      onCollaboratorsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleToggleLinkSharing = async (enabled) => {
    setSavingSettings(true);
    try {
      const newSettings = {
        access_type: enabled ? 'link' : 'private',
        link_permission: shareSettings.link_permission || 'view',
      };
      const response = await collaborationApi.updateShareSettings(spaceId, newSettings);
      setShareSettings(response.data);
      toast.success(enabled ? 'Link sharing enabled' : 'Link sharing disabled');
    } catch (err) {
      toast.error('Failed to update sharing settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePermissionChange = async (permission) => {
    setSavingSettings(true);
    try {
      const response = await collaborationApi.updateShareSettings(spaceId, {
        access_type: shareSettings.access_type,
        link_permission: permission,
      });
      setShareSettings(response.data);
      toast.success(`Link permission updated to ${permission}`);
    } catch (err) {
      toast.error('Failed to update permission');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRegenerateLink = async () => {
    setSavingSettings(true);
    try {
      const response = await collaborationApi.regenerateLink(spaceId);
      setShareSettings(prev => ({ ...prev, public_link_token: response.data.public_link_token }));
      toast.success('New link generated. Old link no longer works.');
    } catch (err) {
      toast.error('Failed to regenerate link');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRemoveGuest = async (guestId) => {
    try {
      await collaborationApi.removeGuest(spaceId, guestId);
      setGuests(prev => prev.filter(g => g.guest_id !== guestId));
      toast.success('Guest removed');
    } catch (err) {
      toast.error('Failed to remove guest');
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/collaboration/join/${shareSettings.public_link_token}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
    toast.success('Link copied to clipboard');
  };

  const handleCopyInviteLink = (token) => {
    const link = `${window.location.origin}/collaboration/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invite link copied');
  };

  const publicLink = shareSettings.public_link_token
    ? `${window.location.origin}/collaboration/join/${shareSettings.public_link_token}`
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(colors?.bgCard, "max-w-lg")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors?.textPrimary)}>
            <Users className="w-5 h-5" />
            Share &quot;{spaceName}&quot;
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="w-full">
            <TabsTrigger value="people" className="flex-1">
              <UserPlus className="w-4 h-4 mr-1" />
              People
            </TabsTrigger>
            <TabsTrigger value="link" className="flex-1">
              <Link2 className="w-4 h-4 mr-1" />
              Get Link
            </TabsTrigger>
            {guests.length > 0 && (
              <TabsTrigger value="guests" className="flex-1">
                <Users className="w-4 h-4 mr-1" />
                Guests ({guests.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* People Tab - Invite via email */}
          <TabsContent value="people" className="mt-4 space-y-4">
            {/* Email Invite */}
            <div>
              <Label className={cn("text-sm mb-2 block", colors?.textSecondary)}>
                Invite via email
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInviteEmail()}
                  className={cn("flex-1", colors?.bgInput, colors?.borderPrimary)}
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className={cn(
                    "px-3 py-2 rounded-md border text-sm",
                    colors?.bgInput,
                    colors?.borderPrimary,
                    colors?.textPrimary
                  )}
                >
                  <option value="edit">Can Edit</option>
                  <option value="view">Can View</option>
                </select>
                <Button onClick={handleInviteEmail} disabled={inviting || !inviteEmail.trim()}>
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>
              <p className={cn("text-xs mt-1", colors?.textMuted)}>
                They&apos;ll receive an email with a private invitation link
              </p>
            </div>

            {/* Current Collaborators */}
            <div>
              <Label className={cn("text-sm mb-2 block", colors?.textSecondary)}>
                People with access
              </Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {collaborators.map((collab, i) => (
                  <div
                    key={collab.user_id || collab.email || i}
                    className={cn(
                      "flex items-center justify-between p-2 rounded-lg",
                      colors?.bgSecondary
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                        {(collab.name || collab.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className={cn("text-sm font-medium", colors?.textPrimary)}>
                          {collab.name || collab.email?.split('@')[0]}
                        </p>
                        <p className={cn("text-xs", colors?.textMuted)}>
                          {collab.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={collab.role === 'owner' ? 'default' : 'secondary'}>
                        {collab.role || 'editor'}
                      </Badge>
                      {!collab.joined_at && (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {collaborators.length === 0 && (
                  <p className={cn("text-sm text-center py-4", colors?.textMuted)}>
                    No collaborators yet
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Link Tab - Public link sharing */}
          <TabsContent value="link" className="mt-4 space-y-4">
            {loadingSettings ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <>
                {/* Link Sharing Toggle */}
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-lg",
                  colors?.bgSecondary
                )}>
                  <div className="flex items-center gap-3">
                    {shareSettings.access_type === 'link' ? (
                      <Globe className="w-5 h-5 text-green-500" />
                    ) : (
                      <Lock className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <p className={cn("font-medium", colors?.textPrimary)}>
                        {shareSettings.access_type === 'link' ? 'Anyone with link' : 'Restricted'}
                      </p>
                      <p className={cn("text-xs", colors?.textMuted)}>
                        {shareSettings.access_type === 'link'
                          ? 'Anyone with the link can access this space'
                          : 'Only invited people can access'}
                      </p>
                    </div>
                  </div>
                  {isOwner && (
                    <Switch
                      checked={shareSettings.access_type === 'link'}
                      onCheckedChange={handleToggleLinkSharing}
                      disabled={savingSettings}
                    />
                  )}
                </div>

                {/* Link Permission */}
                {shareSettings.access_type === 'link' && isOwner && (
                  <div className="flex items-center gap-2">
                    <Label className={cn("text-sm", colors?.textSecondary)}>
                      Permission:
                    </Label>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant={shareSettings.link_permission === 'view' ? 'default' : 'outline'}
                        onClick={() => handlePermissionChange('view')}
                        disabled={savingSettings}
                      >
                        Can view
                      </Button>
                      <Button
                        size="sm"
                        variant={shareSettings.link_permission === 'edit' ? 'default' : 'outline'}
                        onClick={() => handlePermissionChange('edit')}
                        disabled={savingSettings}
                      >
                        Can edit
                      </Button>
                    </div>
                  </div>
                )}

                {/* Copy Link */}
                {shareSettings.access_type === 'link' && shareSettings.public_link_token && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={publicLink}
                        readOnly
                        className={cn("text-sm", colors?.bgInput)}
                      />
                      <Button onClick={handleCopyLink} variant="outline">
                        {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleRegenerateLink}
                        disabled={savingSettings}
                        className="text-xs"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Regenerate link (revokes old link)
                      </Button>
                    )}
                  </div>
                )}

                {shareSettings.access_type !== 'link' && (
                  <p className={cn("text-sm text-center py-4", colors?.textMuted)}>
                    Enable link sharing to let anyone with the link join this space
                  </p>
                )}
              </>
            )}
          </TabsContent>

          {/* Guests Tab */}
          <TabsContent value="guests" className="mt-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {guests.map((guest) => (
                <div
                  key={guest.guest_id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg",
                    colors?.bgSecondary
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full bg-cover bg-no-repeat border-2 border-white shadow-sm"
                      style={getAvatarStyle(guest.avatar_id || 1)}
                    />
                    <div>
                      <p className={cn("font-medium", colors?.textPrimary)}>
                        {guest.name}
                      </p>
                      <p className={cn("text-xs", colors?.textMuted)}>
                        Joined {new Date(guest.joined_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {guest.role || 'view'}
                    </Badge>
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveGuest(guest.guest_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {guests.length === 0 && (
                <p className={cn("text-sm text-center py-4", colors?.textMuted)}>
                  No guests have joined via link yet
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
